import type { Currency } from 'types';

export interface SectionView {
  /** If the section has subsections */
  containsSubsections?: boolean;
  /** The Display name of section */
  displayName?: string;
  /** The name of section */
  name?: string;
  /** The section subdomain */
  subdomain?: string;
  /** The section description */
  description?: string;
  /** : The section id */
  id?: number;
  /** The section title */
  title?: string;
}

interface Link {
  /** The link url for an action */
  action?: string;
  /** Whether the link should be opened externally */
  external?: boolean;
  /** The link text */
  title: string;
  /** Link to open on web */
  url?: string;
  partnerName?: string;
  partnerType?: string;
}

export interface ServicePartner {
  iconPoweredBy?: string;
  link?: Link;
  name: string;
  iconServicePartner?: string;
  title: string;
}

export interface GreenlightView {
  listBlock?: ListBlock;
  titleGroup: TitleGroup;
  infoLink?: InfoLink;
}

export interface ListBlock {
  items: ListItem[];
  extraInfo?: { text: string };
}

export interface ListItem {
  icon?: string;
  body?: string;
  title?: string;
}

export interface TitleGroup {
  icon?: string;
  title?: string;
  subtitle?: string;
}

export interface InfoLink {
  external?: boolean;
  text?: string;
  url?: string;
}
export interface DealerSummary {
  id: number;
  address: string;
  enhancedAddress?: {
    streetAddress: string | null;
    addressLocality: string | null;
    addressRegion: string | null;
    postalCode: string | null;
  };
  description?: string;
  type?: string;
  openingHours?: string;
  enhancedOpeningHours?: EnhancedOpeningHours;
  longitude?: number;
  latitude?: number;
  websiteURL?: string;
  otherAds?: boolean;
  totalAds?: number;
  establishedYear?: number;
  franchiseType?: string;
  showMiniShowroom?: boolean;
  showDealerCard?: boolean;
  showEnquiryFormTradeIn?: boolean;
  showroomUrl: string;
  closedNow?: boolean;
  forecourtPhoto?: {
    small: string;
    large: string;
    medium: string;
  };
  name: string;
  logo?: {
    small?: string;
    medium?: string;
  };
  encryptedPhone?: string;
  franchisesDisplay?: string;
  franchiseCount?: number;
  googleReview?: GoogleReviews;
  trustedDealer?: boolean;
}

export interface EnhancedOpeningHours {
  days: {
    day:
      | 'Monday'
      | 'Tuesday'
      | 'Wednesday'
      | 'Thursday'
      | 'Friday'
      | 'Saturday'
      | 'Sunday';
    openTime?: string;
    closeTime?: string;
    appointmentOnly?: boolean;
    appointmentOnlyText?: string;
  }[];
}

export interface CardPayment {
  stripeAccountId?: string;
  quantity?: string;
  shippingFee?: string;
  shippingAvailable?: boolean;
  cardPaymentType?: string;
  acceptPayment?: boolean;
}
export interface FinanceInfo {
  title: string;
  buttonText: string;
  externalRedirectURL?: string;
  internalRedirectURL?: string;
  imageUrl: string;
  pricePerMonth: {
    priceDisplay: string;
    title: string;
    displayText: string;
    buttonText: string;
    buttonUrl: string;
  };
  external?: boolean;
  partnerName: string;
  partnerType: string;
  carStock?: string;
}

export interface FinanceLink {
  title: string;
  url: string;
}

export interface UserVerification {
  phone?: boolean;
  email?: boolean;
  bank?: boolean;
  allowedAccess?: boolean;
}

interface Chip {
  name: string;
  count: number;
}

interface ReviewAggregation {
  displayName: string;
  totalCount: number;
  chips?: Chip[];
}

export interface RatingStarDistribution {
  fiveStars: number;
  fourStars: number;
  threeStars: number;
  twoStars: number;
  oneStar: number;
}

export type ReviewChipAggregationKeys = 'positive' | 'negative';

type ReviewChipAggregation = {
  [key in ReviewChipAggregationKeys]: ReviewAggregation;
};

type UserRating = {
  average?: string;
  reviewCount: number;
  ratingCategory: 'Poor' | 'Fair' | 'Okay' | 'Good' | 'Excellent';
  ratingStarDistribution: RatingStarDistribution;
  reviewChipAggregation?: ReviewChipAggregation;
};

export const SELLER_TYPES = {
  PRIVATE: 'PRIVATE',
  TRADER: 'TRADER',
  DEALER: 'DEALER',
} as const;

export type SellerTypes = (typeof SELLER_TYPES)[keyof typeof SELLER_TYPES];

export interface UserSummary {
  id: number;
  name?: string;
  type?: SellerTypes;
  county?: string;
  verification?: UserVerification;
  avgResponseRate?: string;
  adCountStats?: {
    activeAdView?: {
      displayText?: string;
      value?: string;
    };
    lifetimeAdView?: {
      displayText?: string;
      value?: string;
    };
  };
  registrationDate?: string;
  countyTown?: string | null;
  traderName?: string;
  rating?: UserRating;
  membershipDuration?: string;
}

export interface ApprovedProgrammeView {
  id?: string;
  hero?: {
    photo?: {
      small: string;
      large: string;
    };
    logo?: string;
    title?: string;
    subtitle?: string;
  };
  benefits?: string[];
  moreInfo?: Link;
  description?: string;
  terms?: {
    text: string;
    link: Link;
  };
  className?: string;
}

export interface GoogleReviews {
  rating: string;
  url: string;
  userRatingsTotal: string;
  reviewList: ReviewList[];
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
    viewport: {
      northeast: {
        lat: number;
        lng: number;
      };
      southwest: {
        lat: number;
        lng: number;
      };
    };
  };
  displayReviews?: boolean;
  cid: string;
}

export interface ReviewList {
  authorName: string;
  rating: number;
  relativeTimeDescription: string;
  text: string;
}

export type { UserRating };

export const FEEDBACK_TRACKING_SOURCES = {
  mobileAvgRatingReviewScore: 'mobile_avg_rating_review_score',
  desktopAvgRatingScoreTop: 'desktop_avg_rating_score_top',
  desktopReviewCountTop: 'desktop_review_count_top',
  ratingScoreBottom: 'avg_rating_score_bottom',
  reviewCountBottom: 'review_count_bottom',
  topPanelTitle: 'top_panel_title',
  sidePanelTitle: 'side_panel_title',
  topPanelSubtitle: 'top_panel_subtitle',
  topPanelIcon: 'top_panel_icon',
} as const;

export type FeedbackTrackingSourcesNames =
  (typeof FEEDBACK_TRACKING_SOURCES)[keyof typeof FEEDBACK_TRACKING_SOURCES];

export const AUTHENTICATION_STATUSES = {
  AUTHENTICATED: 'authenticated',
  UNAUTHENTICATED: 'unauthenticated',
  LOADING: 'loading',
} as const;

export type AuthenticationStatuses =
  (typeof AUTHENTICATION_STATUSES)[keyof typeof AUTHENTICATION_STATUSES];

export const RESERVATION_STATUS = {
  ENABLED: 'enabled',
  DISABLED: 'disabled',
} as const;

export type ReservationStatuses =
  (typeof RESERVATION_STATUS)[keyof typeof RESERVATION_STATUS];

export type Reservation = {
  value: number;
  status: ReservationStatuses;
  currency: Currency;
};
